import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import {
  CircleStackIcon,
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import {
  AmsRefKind,
  CaraWorkspaceAmsContextInput,
  CaraWorkspaceAmsRefInput,
  CaraWorkspaceExistingFileInput,
  CaraWorkspaceFileInput,
  KnowledgeGraphEntityType,
  SearchFileResultFieldsFragment
} from '~/api/graphql';
import { getLaunchClientFiles, searchExistingFiles } from '~/api/data/documents/queries';
import { type LaunchClient, useLaunchClient } from './launch-client';
import { Badge } from '@oysterjs/ui/badge';
import { Input, InputGroup } from '@oysterjs/ui/input';
import {
  AmsDocumentPicker,
  type AmsContextPinSelection,
  type AmsDocumentSelection,
  type AmsPickerConfirmResult
} from './ams-picker';
import { entityTypeIcon } from './ams-picker/entity-display';
import {
  amsContextFromPickerConfirm,
  amsRefsFromPickerConfirm
} from '~/components/cara-input/cara-attachment';
import { amsRefKey } from './ams-picker/ams-ref';
import { integrationName } from '~/lib/integrations';

export type FileInputChange = {
  files: CaraWorkspaceFileInput[];
  amsContext: CaraWorkspaceAmsContextInput[];
  /** The AMS browser's picks, sent by their AMS identity. */
  amsRefs?: CaraWorkspaceAmsRefInput[];
};

export const emptyFileInputChange = (): FileInputChange => ({
  files: [],
  amsContext: [],
  amsRefs: []
});

/** True when the lane holds anything that reaches the workspace: an upload, a library file, or an AMS pin. */
export const hasFileInputContent = (change: FileInputChange): boolean =>
  change.files.length > 0 || change.amsContext.length > 0 || (change.amsRefs?.length ?? 0) > 0;

/**
 * True when the lane holds a document the workspace will attach: an upload, a
 * library file, or an AMS file pin, which the server attaches as an existing
 * file. A pinned account or policy is context, not a document.
 */
export const hasFileInputDocument = (change: FileInputChange): boolean =>
  change.files.length > 0 ||
  change.amsContext.some((pin) => pin.entityType === KnowledgeGraphEntityType.File) ||
  (change.amsRefs ?? []).some((pin) => pin.ref.kind === AmsRefKind.File);

/** Merges multiple FileInput fields (e.g. certificate + quote uploads) for workspace create. */
export const mergeFileInputChanges = (...parts: FileInputChange[]): FileInputChange => {
  const files = parts.flatMap((part) => part.files);
  const seen = new Set<string>();
  const amsContext: CaraWorkspaceAmsContextInput[] = [];
  const amsRefs: CaraWorkspaceAmsRefInput[] = [];
  for (const part of parts) {
    for (const entry of part.amsContext) {
      if (seen.has(entry.entityId)) {
        continue;
      }
      seen.add(entry.entityId);
      amsContext.push(entry);
    }
    for (const entry of part.amsRefs ?? []) {
      const key = amsRefKey(entry.ref);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      amsRefs.push(entry);
    }
  }
  return { files, amsContext, amsRefs };
};

const formatSize = (size: number) => {
  if (size < 1024) {
    return `${size} B`;
  } else if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  } else if (size < 1024 * 1024 * 1024) {
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  } else {
    return `${(size / 1024 / 1024 / 1024).toFixed(1)} GB`;
  }
};

const getFilesFromEvent = (dataTransfer?: DataTransfer): File[] => {
  const files: File[] = [];

  if (dataTransfer && dataTransfer.items) {
    // Use DataTransferItemList interface to access the file(s)
    for (let i = 0; i < dataTransfer.items.length; i++) {
      // If dropped items aren't files, reject them
      if (dataTransfer.items[i].kind === 'file') {
        const file = dataTransfer.items[i].getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }
  } else if (dataTransfer && dataTransfer.files) {
    // Use DataTransfer interface to access the file(s)
    for (let i = 0; i < dataTransfer.files.length || 0; i++) {
      const file = dataTransfer.files[i];
      if (file) {
        files.push(file);
      }
    }
  }

  return files;
};

const amsSelectionCount = (files: AmsDocumentSelection[], contextPins: AmsContextPinSelection[]) =>
  files.length + contextPins.length;

const buildChange = (
  attachments: File[],
  existingFiles: CaraWorkspaceExistingFileInput[],
  amsFiles: AmsDocumentSelection[],
  contextPins: AmsContextPinSelection[]
): FileInputChange => ({
  files: [
    ...attachments.map((f) => ({ upload: f })),
    ...existingFiles.map((e) => ({ existing: e }))
  ] as CaraWorkspaceFileInput[],
  // AMS picks travel only as pins; the server turns FILE pins into workspace
  // files, so listing them under files too would attach each twice.
  amsContext: amsContextFromPickerConfirm({ files: amsFiles, contextPins }),
  amsRefs: amsRefsFromPickerConfirm({ files: amsFiles, contextPins })
});

/**
 * The launch client's own uploads, newest first, as search results. A failure
 * leaves the general search to stand alone rather than failing it.
 */
const searchLaunchClientFiles = async (
  launchClient: LaunchClient | null,
  search: string
): Promise<SearchFileResultFieldsFragment[]> => {
  if (!launchClient) return [];
  try {
    const result = await getLaunchClientFiles(launchClient.id, search);
    return result.error ? [] : (result.data ?? []);
  } catch {
    return [];
  }
};

export const FileInput = ({
  className,
  onChange,
  invalid,
  multiple = false,
  enableExisting = false,
  enableAMSSearch = false,
  enableUpload = true,
  compact = false
}: {
  className?: string;
  onChange: (change: FileInputChange) => void;
  invalid?: boolean;
  multiple?: boolean;
  enableExisting?: boolean;
  /** When true, shows a "Browse AMS" control that opens AmsDocumentPicker. */
  enableAMSSearch?: boolean;
  /**
   * When false, hides the upload affordance so only existing files can be
   * picked (pair with `enableExisting`). `className` and `invalid` style the
   * drop zone, so both are inert in this mode.
   */
  enableUpload?: boolean;
  /**
   * Stacked upload lanes. Search stays hidden until asked for — the default
   * layout still shows search immediately (full-page compose).
   */
  compact?: boolean;
}) => {
  const [dragging, setDragging] = React.useState(false);
  const [attachments, setAttachments] = React.useState<File[]>([]);
  const [existingFiles, setExistingFiles] = React.useState<CaraWorkspaceExistingFileInput[]>([]);
  const [amsFiles, setAmsFiles] = React.useState<AmsDocumentSelection[]>([]);
  const [contextPins, setContextPins] = React.useState<AmsContextPinSelection[]>([]);
  const [amsPickerOpen, setAmsPickerOpen] = React.useState(false);
  const [emptyFileNames, setEmptyFileNames] = React.useState<string[]>([]);
  const [searchResults, setSearchResults] = React.useState<SearchFileResultFieldsFragment[]>([]);
  const launchClient = useLaunchClient();
  const [clientFileIds, setClientFileIds] = React.useState<ReadonlySet<string>>(new Set());
  // A client with no files would otherwise refetch on every focus.
  const emptyClientIdRef = React.useRef<string | null>(null);
  // Only the newest search may write results, so a slow answer for an earlier
  // query or an earlier client cannot replace a newer one.
  const searchRequestRef = React.useRef(0);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  // Compact starts with search hidden; the full-page compose box starts open.
  const [searchOpen, setSearchOpen] = React.useState(!compact);
  const [fileDataMap, setFileDataMap] = React.useState<Map<string, SearchFileResultFieldsFragment>>(
    new Map()
  );
  const ref = React.useRef<HTMLInputElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const emitChange = (
    files: File[],
    nextExistingFiles: CaraWorkspaceExistingFileInput[],
    nextAmsFiles: AmsDocumentSelection[] = amsFiles,
    nextContextPins: AmsContextPinSelection[] = contextPins
  ) => {
    onChange(buildChange(files, nextExistingFiles, nextAmsFiles, nextContextPins));
  };

  const addFiles = (files: File[]) => {
    const empty = files.filter((f) => f.size === 0);
    const valid = files.filter((f) => f.size > 0);
    setEmptyFileNames(empty.map((f) => f.name));
    if (valid.length === 0) return;
    setAttachments((prev) => {
      const newAttachments = multiple ? [...prev, ...valid] : [...valid];
      emitChange(newAttachments, existingFiles);
      return newAttachments;
    });
  };

  const removeFile = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation();
    setAttachments((prev) => {
      const newAttachments = prev.filter((_, i) => i !== index);
      emitChange(newAttachments, existingFiles);
      return newAttachments;
    });
  };

  const removeExistingFile = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation();
    setExistingFiles((prev) => {
      const newExistingFiles = prev.filter((_, i) => i !== index);
      emitChange(attachments, newExistingFiles);
      return newExistingFiles;
    });
  };

  const removeAmsFile = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation();
    setAmsFiles((prev) => {
      const nextAmsFiles = prev.filter((_, i) => i !== index);
      emitChange(attachments, existingFiles, nextAmsFiles, contextPins);
      return nextAmsFiles;
    });
  };

  const removeContextPin = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation();
    setContextPins((prev) => {
      const nextContextPins = prev.filter((_, i) => i !== index);
      emitChange(attachments, existingFiles, amsFiles, nextContextPins);
      return nextContextPins;
    });
  };

  const addExistingFile = (file: SearchFileResultFieldsFragment) => {
    // Store file data for later reference
    setFileDataMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(file.id, file);
      return newMap;
    });

    setExistingFiles((prev) => {
      const alreadySelected = prev.some((f) => f.id === file.id);
      if (alreadySelected) {
        return prev;
      }
      const newExistingFiles = multiple
        ? [...prev, { id: file.id, integrationId: file.integration?.id }]
        : [{ id: file.id, integrationId: file.integration?.id }];
      emitChange(multiple ? attachments : [], newExistingFiles);
      return newExistingFiles;
    });
    if (!multiple) {
      setAttachments([]);
    }
  };

  const onAmsConfirm = (result: AmsPickerConfirmResult) => {
    setAmsFiles(result.files);
    setContextPins(result.contextPins);
    emitChange(attachments, existingFiles, result.files, result.contextPins);
  };

  const onDragEnter = (e: { dataTransfer?: DataTransfer; preventDefault: () => void }) => {
    e.preventDefault();
    setDragging(true);
  };

  const onDragLeave = (e: { dataTransfer?: DataTransfer; preventDefault: () => void }) => {
    e.preventDefault();
    setDragging(false);
  };

  const onDragOver = (e: { dataTransfer?: DataTransfer; preventDefault: () => void }) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const onDrop = (e: { dataTransfer?: DataTransfer; preventDefault: () => void }) => {
    e.preventDefault();
    setDragging(false);

    const files = getFilesFromEvent(e.dataTransfer);
    if (files.length > 0) {
      addFiles(files);
    }
  };

  const _onSearch = async (search: string) => {
    const request = ++searchRequestRef.current;
    // With a client, an empty search still lists that client's newest files, so
    // opening the search is enough to find the usual ones.
    if (!search.trim() && !launchClient) {
      setSearchResults([]);
      setDropdownOpen(false);
      return;
    }
    try {
      // todo loading indicator
      const [clientFiles, res] = await Promise.all([
        searchLaunchClientFiles(launchClient, search),
        search.trim() ? searchExistingFiles(search) : Promise.resolve(null)
      ]);
      if (request !== searchRequestRef.current) return;
      if (res?.error) throw res.error;
      if (launchClient && !search.trim() && clientFiles.length === 0) {
        emptyClientIdRef.current = launchClient.id;
      }
      const clientIds = new Set(clientFiles.map((file) => file.id));
      const results = [
        ...clientFiles,
        ...(res?.data ?? []).filter((file) => !clientIds.has(file.id))
      ];
      // Update file data map with new search results
      setFileDataMap((prev) => {
        const newMap = new Map(prev);
        results.forEach((file) => {
          newMap.set(file.id, file);
        });
        return newMap;
      });
      setClientFileIds(clientIds);
      setSearchResults(results);
      setDropdownOpen(results.length > 0);
    } catch {
      if (request !== searchRequestRef.current) return;
      setSearchResults([]);
      setDropdownOpen(false);
    }
  };

  // The debounced call reads the latest _onSearch, so a client that changes
  // within the delay is the one searched.
  const onSearchRef = React.useRef(_onSearch);
  onSearchRef.current = _onSearch;
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const onSearch = React.useCallback((content: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => onSearchRef.current(content), 300);
  }, []);

  // Results and badges belong to the client they were fetched for.
  React.useEffect(() => {
    searchRequestRef.current += 1;
    setSearchResults([]);
    setClientFileIds(new Set());
    setDropdownOpen(false);
  }, [launchClient?.id]);

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isFileSelected = (fileId: string) => {
    return existingFiles.some((f) => f.id === fileId);
  };

  const amsSelectionTotal = amsSelectionCount(amsFiles, contextPins);
  const totalFiles =
    attachments.length + existingFiles.length + amsFiles.length + contextPins.length;
  const hasFiles = totalFiles > 0;

  const fileChips = hasFiles && (
    <div
      className={clsx(
        'flex flex-wrap flex-row gap-2 items-center',
        compact ? 'pb-2' : 'pt-2',
        enableUpload && !compact && 'justify-center'
      )}
    >
      {attachments.map((attachment, i) => (
        <div
          key={`upload-${attachment.name}-${i}`}
          className="rounded-full bg-neutral-100 dark:bg-neutral-700 px-3 py-1 cursor-default flex flex-row items-center justify-center gap-1 max-w-full min-w-0"
          title={`${attachment.name} (${formatSize(attachment.size)})`}
        >
          <p className="text-xs text-neutral-500 dark:text-neutral-400 flex flex-row items-baseline gap-1 min-w-0">
            <span className="truncate max-w-[16rem]">{attachment.name}</span>
            <span className="shrink-0">({formatSize(attachment.size)})</span>
          </p>
          <Headless.Button type="button" onClick={(e) => removeFile(e, i)} className="shrink-0">
            <XMarkIcon className="size-3" />
          </Headless.Button>
        </div>
      ))}
      {existingFiles.map((existingFile, i) => {
        const fileInfo = fileDataMap.get(existingFile.id);
        const displayName = fileInfo?.name || existingFile.id;
        return (
          <div
            key={`existing-${existingFile.id}-${i}`}
            className="rounded-full bg-neutral-100 dark:bg-neutral-700 px-3 py-1 cursor-default flex flex-row items-center justify-center gap-1 max-w-full min-w-0"
            title={displayName}
          >
            <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate max-w-[16rem] min-w-0">
              {displayName}
            </p>
            <Headless.Button
              type="button"
              aria-label={`Remove ${displayName}`}
              onClick={(e) => removeExistingFile(e, i)}
              className="shrink-0"
            >
              <XMarkIcon className="size-3" />
            </Headless.Button>
          </div>
        );
      })}
      {amsFiles.map((amsFile, i) => (
        <div
          key={`ams-${amsFile.id}`}
          data-testid="ams-selected-file"
          className="flex max-w-full min-w-0 cursor-default flex-row items-center justify-center gap-1.5 rounded-full bg-sky-100 dark:bg-sky-500/20 px-3 py-1 text-sky-700 dark:text-sky-300"
          title={`${amsFile.displayName} · ${integrationName(amsFile.integrationType)}`}
        >
          <CircleStackIcon
            data-testid="ams-source-icon"
            className="size-3.5 shrink-0"
            aria-hidden="true"
          />
          <p className="flex min-w-0 flex-row items-baseline gap-1 text-xs">
            <span className="max-w-[16rem] truncate">{amsFile.displayName}</span>
            <span className="shrink-0 text-sky-600 dark:text-sky-400">
              ({integrationName(amsFile.integrationType)})
            </span>
          </p>
          <Headless.Button
            type="button"
            onClick={(e) => removeAmsFile(e, i)}
            className="shrink-0"
            aria-label={`Remove ${amsFile.displayName}`}
          >
            <XMarkIcon className="size-3" />
          </Headless.Button>
        </div>
      ))}
      {contextPins.map((pin, i) => (
        <div
          key={`pin-${pin.id}`}
          data-testid="ams-selected-context-pin"
          className="flex max-w-full min-w-0 cursor-default flex-row items-center justify-center gap-1.5 rounded-full bg-sky-100 dark:bg-sky-500/20 px-3 py-1 text-sky-700 dark:text-sky-300"
          title={`${pin.displayName} · ${integrationName(pin.integrationType)}`}
        >
          {entityTypeIcon(pin.entityType, 'size-3.5 shrink-0')}
          <p className="flex min-w-0 flex-row items-baseline gap-1 text-xs">
            <span className="max-w-[16rem] truncate">{pin.displayName}</span>
            <span className="shrink-0 text-sky-600 dark:text-sky-400">
              ({integrationName(pin.integrationType)})
            </span>
          </p>
          <Headless.Button
            type="button"
            onClick={(e) => removeContextPin(e, i)}
            className="shrink-0"
            aria-label={`Remove ${pin.displayName}`}
          >
            <XMarkIcon className="size-3" />
          </Headless.Button>
        </div>
      ))}
    </div>
  );

  return (
    <div data-slot="control" className={clsx('flex w-full flex-col', compact ? 'gap-2' : 'gap-3')}>
      {enableUpload && (
        <Headless.Input
          ref={ref}
          type="file"
          onChange={(e) => addFiles(Array.from(e.currentTarget?.files || []))}
          multiple={multiple}
          className="hidden"
        />
      )}
      {enableExisting && searchOpen && (
        <div className="w-full relative">
          <InputGroup>
            <MagnifyingGlassIcon />
            <Input
              ref={searchInputRef}
              name="search"
              placeholder="Search for existing files&hellip;"
              aria-label="Search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                onSearch(e.target.value);
              }}
              onFocus={() => {
                if (searchResults.length > 0) {
                  setDropdownOpen(true);
                } else if (
                  launchClient &&
                  !searchQuery.trim() &&
                  emptyClientIdRef.current !== launchClient.id
                ) {
                  void _onSearch('');
                }
              }}
            />
          </InputGroup>
          {dropdownOpen && searchResults.length > 0 && (
            <div
              ref={dropdownRef}
              className="absolute z-10 mt-1 w-full max-h-80 overflow-auto rounded-lg bg-white dark:bg-neutral-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div className="py-1">
                {searchResults.slice(0, 10).map((file) => (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => {
                      addExistingFile(file);
                    }}
                    className={clsx(
                      'w-full text-left px-4 py-2.5 text-sm hover:bg-primary-500 hover:text-white transition-colors',
                      'flex items-center justify-between group'
                    )}
                  >
                    <span className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="truncate">{file.name}</span>
                      {launchClient && clientFileIds.has(file.id) && (
                        <Badge color="emerald" className="min-w-0 max-w-48 shrink-0">
                          <span className="truncate">{launchClient.name}</span>
                        </Badge>
                      )}
                      {file.integration && (
                        <Badge color="sky" className="shrink-0">
                          {integrationName(file.integration.type)}
                        </Badge>
                      )}
                    </span>
                    <svg
                      className={clsx(
                        'size-5 shrink-0 ml-2',
                        isFileSelected(file.id)
                          ? 'text-primary-600 dark:text-primary-400 group-hover:text-white'
                          : 'text-transparent'
                      )}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {compact ? (
        // One lane: the chips, then the affordances on a single row. Dropping is
        // still handled by the whole lane, but clicking is not -- the row holds
        // three separate actions, so a lane-wide click would fire the wrong one.
        <div
          onDragEnter={enableUpload ? onDragEnter : undefined}
          onDragLeave={enableUpload ? onDragLeave : undefined}
          onDragOver={enableUpload ? onDragOver : undefined}
          onDrop={enableUpload ? onDrop : undefined}
          className={clsx(
            'rounded-lg px-3 py-2.5 transition-all duration-100',
            invalid && 'outline outline-2 outline-red-600',
            !invalid &&
              !dragging &&
              'outline-dashed outline-1 outline-neutral-950/10 dark:outline-white/10 hover:outline-neutral-950/20 dark:hover:outline-white/20',
            !invalid && dragging && 'outline outline-2 outline-primary-500',
            className
          )}
        >
          {fileChips}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs/5">
            {enableUpload && (
              <>
                <span className="inline-flex items-center gap-1.5 text-neutral-500 dark:text-neutral-400">
                  <CloudArrowUpIcon className="size-4" />
                  Drag files here, or
                </span>
                <button
                  type="button"
                  className="font-medium text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
                  onClick={() => ref?.current?.click()}
                >
                  Choose files
                </button>
              </>
            )}
            {enableExisting && !searchOpen && (
              <>
                {enableUpload && (
                  <span className="text-neutral-300 dark:text-neutral-600">&middot;</span>
                )}
                <button
                  type="button"
                  className="font-medium text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
                  onClick={() => setSearchOpen(true)}
                >
                  Search existing files
                </button>
              </>
            )}
            {enableAMSSearch && (
              <>
                {(enableUpload || enableExisting) && (
                  <span className="text-neutral-300 dark:text-neutral-600">&middot;</span>
                )}
                <button
                  type="button"
                  className="font-medium text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
                  onClick={() => setAmsPickerOpen(true)}
                >
                  {amsSelectionTotal > 0 ? `Browse AMS (${amsSelectionTotal})` : 'Browse AMS'}
                </button>
              </>
            )}
          </div>
        </div>
      ) : enableUpload ? (
        <div
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onClick={() => ref?.current?.click()}
          className={clsx([
            'rounded-lg p-4 flex flex-col gap-4',
            'flex flex-col items-center justify-center cursor-pointer',
            'transition-all duration-100',
            invalid && 'outline outline-2 outline-red-600',
            !invalid &&
              !dragging &&
              !hasFiles &&
              'outline-dashed outline-1 outline-neutral-950/10 dark:outline-white/10 hover:outline-neutral-950/20 dark:hover:outline-white/20',
            (dragging || hasFiles) && 'outline outline-2 outline-primary-500',
            className
          ])}
        >
          <div>
            <div className="pointer-events-none flex flex-col items-center justify-center">
              <CloudArrowUpIcon
                className={
                  hasFiles
                    ? 'size-4 text-sky-500 dark:text-sky-400'
                    : 'size-8 text-neutral-500 dark:text-neutral-400'
                }
              />
              <p
                className={
                  hasFiles
                    ? 'text-xs text-sky-600 dark:text-sky-400'
                    : 'text-sm text-neutral-500 dark:text-neutral-400'
                }
              >
                {hasFiles ? 'Drop or click to add more files' : 'Drag and drop files here'}
              </p>
            </div>
            {fileChips}
          </div>
        </div>
      ) : (
        fileChips
      )}
      {emptyFileNames.map((name) => (
        <p key={name} className="text-sm/5 text-red-600 sm:text-xs/4 dark:text-red-500">
          &ldquo;{name}&rdquo; is empty. Please upload a readable file.
        </p>
      ))}
      {enableAMSSearch && (
        <>
          {!compact && (
            <button
              type="button"
              className="self-start text-sm font-medium text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
              onClick={() => setAmsPickerOpen(true)}
            >
              {amsSelectionTotal > 0 ? `Browse AMS (${amsSelectionTotal} selected)` : 'Browse AMS'}
            </button>
          )}
          {amsPickerOpen && (
            <AmsDocumentPicker
              open
              onClose={() => setAmsPickerOpen(false)}
              initialSelectedFiles={amsFiles}
              initialContextPins={contextPins}
              onConfirm={onAmsConfirm}
            />
          )}
        </>
      )}
    </div>
  );
};
